import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ShieldCheck, Lock, Heart, CheckCircle2,
    Users, FileText, ArrowLeft, Mail,
    Sparkles, Eye,
    HeartHandshake, ChevronRight,
    MessageCircle, ShieldAlert
} from 'lucide-react';
import AnimatedSection from '../../components/common/AnimatedSection';

const PrivacyPolicy: React.FC = () => {
    const [activeSection, setActiveSection] = useState<string>('intro');

    const sections = [
        { id: 'intro', title: '1. Pengantar & Prinsip Amanah' },
        { id: 'data-collected', title: '2. Data yang Kami Kumpulkan' },
        { id: 'purpose', title: '3. Tujuan Penggunaan Data' },
        { id: 'confidentiality', title: '4. Kerahasiaan & Ruang Curhat' },
        { id: 'sharing', title: '5. Pembagian Data & Pihak Ketiga' },
        { id: 'rights', title: '6. Hak-Hak Anda sebagai Member' },
        { id: 'retention', title: '7. Penyimpanan & Keamanan Data' },
        { id: 'contact', title: '8. Kontak & Pengaduan Privasi' },
    ];

    const scrollTo = (id: string) => {
        setActiveSection(id);
        const element = document.getElementById(id);
        if (element) {
            const yOffset = -100;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
        }
    };

    return (
        <div className="bg-[#FAF9F5] min-h-screen pt-28 pb-24 text-gray-800 font-sans selection:bg-khazanah-gold/20 selection:text-khazanah-dark">
            {/* HERO SECTION */}
            <AnimatedSection isHero={true} className="relative overflow-hidden bg-gradient-to-br from-khazanah-dark via-khazanah-green to-[#0A3D20] text-white py-16 md:py-24 border-b border-khazanah-gold/30">
                {/* Background Pattern Elements */}
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#C5A85C_1px,transparent_1px)] [background-size:20px_20px] pointer-events-none" />
                <div className="absolute -top-24 -right-24 w-96 h-96 bg-khazanah-gold/15 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />

                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="flex items-center gap-2 mb-6">
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 text-xs md:text-sm text-khazanah-gold hover:text-white transition-colors font-medium bg-white/10 backdrop-blur-xs px-3.5 py-1.5 rounded-full border border-white/10"
                        >
                            <ArrowLeft size={14} /> Kembali ke Beranda
                        </Link>
                        <span className="text-white/40 text-xs">•</span>
                        <span className="text-white/70 text-xs md:text-sm font-medium">Terakhir Diperbarui: 13 Oktober 2026</span>
                    </div>

                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-khazanah-gold/20 border border-khazanah-gold/40 text-khazanah-gold text-xs font-bold uppercase tracking-wider mb-4">
                            <ShieldCheck size={14} /> Dokumen Resmi Perlindungan Data
                        </div>
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-black font-outfit tracking-tight text-white mb-6 leading-tight">
                            Kebijakan <span className="text-transparent bg-clip-text bg-gradient-to-r from-khazanah-gold via-[#E5D3A1] to-khazanah-gold">Privasi</span> Khazanah
                        </h1>
                        <p className="text-base sm:text-lg text-emerald-100/90 leading-relaxed font-normal">
                            Menjaga data pribadi dan privasi curahan hati Anda adalah bagian dari <strong>amanah syar'i</strong> dan komitmen hukum kami. Dokumen ini menjelaskan bagaimana Khazanah Alwahda Kreatif mengumpulkan, menjaga, dan melindungi setiap informasi yang Anda percayakan kepada kami.
                        </p>
                    </div>

                    {/* 3 HIGHLIGHT PILLARS */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
                        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4.5 border border-white/15 flex items-start gap-3.5">
                            <div className="w-10 h-10 rounded-xl bg-khazanah-gold/20 border border-khazanah-gold/40 flex items-center justify-center text-khazanah-gold shrink-0">
                                <Lock size={18} />
                            </div>
                            <div>
                                <h2 className="font-bold text-sm text-white mb-1">Amanah & Terjaga</h2>
                                <p className="text-xs text-white/80 leading-relaxed">
                                    Data dan cerita pribadi tidak akan pernah diperjualbelikan kepada pihak ketiga mana pun.
                                </p>
                            </div>
                        </div>

                        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4.5 border border-white/15 flex items-start gap-3.5">
                            <div className="w-10 h-10 rounded-xl bg-pink-500/20 border border-pink-400/40 flex items-center justify-center text-pink-300 shrink-0">
                                <Heart size={18} />
                            </div>
                            <div>
                                <h2 className="font-bold text-sm text-white mb-1">Kerahasiaan Curhat</h2>
                                <p className="text-xs text-white/80 leading-relaxed">
                                    Kegalauan dan harapan Anda hanya digunakan untuk menyusun program dakwah yang relevan.
                                </p>
                            </div>
                        </div>

                        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-4.5 border border-white/15 flex items-start gap-3.5">
                            <div className="w-10 h-10 rounded-xl bg-teal-500/20 border border-teal-400/40 flex items-center justify-center text-teal-300 shrink-0">
                                <ShieldAlert size={18} />
                            </div>
                            <div>
                                <h2 className="font-bold text-sm text-white mb-1">Kepatuhan UU PDP</h2>
                                <p className="text-xs text-white/80 leading-relaxed">
                                    Sesuai UU No. 27 Tahun 2022 tentang Pelindungan Data Pribadi di Republik Indonesia.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </AnimatedSection>

            {/* MAIN CONTENT WRAPPER */}
            <AnimatedSection className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

                    {/* SIDEBAR NAVIGATION (DESKTOP) */}
                    <div className="lg:col-span-4">
                        <div className="sticky top-28 bg-white rounded-3xl p-6 border border-gray-200/80 shadow-xs space-y-6">
                            <div>
                                <span className="text-[11px] font-bold uppercase tracking-wider text-khazanah-green">Daftar Isi</span>
                                <h2 className="text-lg font-bold text-gray-900 mt-1 font-outfit">Navigasi Kebijakan</h2>
                            </div>

                            <nav className="space-y-1">
                                {sections.map((sec) => (
                                    <button
                                        key={sec.id}
                                        type="button"
                                        onClick={() => scrollTo(sec.id)}
                                        className={`w-full text-left px-3.5 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 flex items-center justify-between group cursor-pointer ${activeSection === sec.id
                                                ? 'bg-khazanah-green text-white shadow-xs'
                                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                            }`}
                                    >
                                        <span className="truncate">{sec.title}</span>
                                        <ChevronRight
                                            size={14}
                                            className={`shrink-0 transition-transform ${activeSection === sec.id ? 'translate-x-0.5 text-khazanah-gold' : 'text-gray-400 group-hover:translate-x-0.5'
                                                }`}
                                        />
                                    </button>
                                ))}
                            </nav>

                            {/* Quick Info Box */}
                            <div className="bg-gradient-to-br from-khazanah-light to-amber-50/50 p-4 rounded-2xl border border-khazanah-gold/30">
                                <div className="flex items-center gap-2 text-khazanah-green font-bold text-xs mb-1.5">
                                    <Sparkles size={14} className="text-khazanah-gold" />
                                    <span>Punya Pertanyaan Privasi?</span>
                                </div>
                                <p className="text-[11px] text-gray-600 leading-relaxed">
                                    Ingin memperbarui, mengonfirmasi, atau menghapus data Anda? Hubungi tim admin kami secara langsung.
                                </p>
                                <a
                                    href="https://wa.me/6281234567890?text=Halo%20Admin%20Khazanah,%20saya%20ingin%20bertanya%20mengenai%20kebijakan%20privasi%20dan%20data%20pendaftaran%20saya"
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-3 inline-flex items-center justify-center gap-1.5 w-full bg-white hover:bg-khazanah-green hover:text-white text-khazanah-green text-xs font-bold py-2 px-3 rounded-xl border border-khazanah-green/30 transition shadow-2xs"
                                >
                                    <MessageCircle size={14} /> Hubungi via WhatsApp
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* CONTENT BODY */}
                    <div className="lg:col-span-8 space-y-12">

                        {/* SECTION 1 */}
                        <section id="intro" className="bg-white rounded-3xl p-8 border border-gray-200/80 shadow-xs space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-xl bg-khazanah-light text-khazanah-green flex items-center justify-center font-bold text-sm">
                                    1
                                </span>
                                <h2 className="text-xl sm:text-2xl font-black font-outfit text-gray-900">
                                    Pengantar & Prinsip Amanah
                                </h2>
                            </div>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-normal">
                                Selamat datang di ekosistem platform <strong>Khazanah Alwahda Kreatif</strong> ("Khazanah", "kami", atau "milik kami"). Kami menaungi berbagai sayap komunitas pemuda Muslim, termasuk komunitas muslimah <strong>Sahabat Sholehah</strong>, komunitas self-growth pemuda <strong>Sobat Jofisah</strong>, program kaderisasi <strong>Khazanah Leaders</strong>, serta ruang interaktif <strong>Ruang Curhat Publik</strong>.
                            </p>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-normal">
                                Kami meyakini bahwa data pribadi dan isi hati yang Anda bagikan adalah amanah. Sebagaimana firman Allah Subhanahu wa Ta'ala:
                            </p>
                            <div className="bg-[#FAF6EE] p-4.5 rounded-2xl border-l-4 border-khazanah-gold text-gray-800 space-y-1.5">
                                <p className="font-amiri text-lg text-right leading-loose text-khazanah-dark">
                                    يَا أَيُّهَا الَّذِينَ آمَنُوا لَا تَخُونُوا اللَّهَ وَالرَّسُولَ وَتَخُونُوا أَمَانَاتِكُمْ وَأَنْتُمْ تَعْلَمُونَ
                                </p>
                                <p className="text-xs sm:text-sm italic font-medium text-gray-700">
                                    "Wahai orang-orang yang beriman! Janganlah kamu mengkhianati Allah dan Rasul, dan (juga) janganlah kamu mengkhianati amanat yang dipercayakan kepadamu, sedang kamu mengetahui."
                                </p>
                                <p className="text-[11px] font-bold text-khazanah-gold text-right">— QS. Al-Anfal: 27</p>
                            </div>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-normal">
                                Dengan mendaftar, mengakses, atau berinteraksi dengan layanan Khazanah, Anda menyatakan telah membaca, memahami, dan menyetujui seluruh ketentuan yang tercantum dalam Kebijakan Privasi ini.
                            </p>
                        </section>

                        {/* SECTION 2: DATA YANG KAMI KUMPULKAN */}
                        <section id="data-collected" className="bg-white rounded-3xl p-8 border border-gray-200/80 shadow-xs space-y-6">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-xl bg-khazanah-light text-khazanah-green flex items-center justify-center font-bold text-sm">
                                    2
                                </span>
                                <h2 className="text-xl sm:text-2xl font-black font-outfit text-gray-900">
                                    Data yang Kami Kumpulkan dari Formulir Pendaftaran
                                </h2>
                            </div>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-normal">
                                Saat Anda mengisi formulir pendaftaran member <strong>Sahabat Sholehah</strong>, <strong>Sobat Jofisah</strong>, atau seleksi <strong>Khazanah Leaders</strong>, kami mengumpulkan kategori data berikut:
                            </p>

                            <div className="space-y-4">
                                {/* Kategori A */}
                                <div className="border border-gray-200 rounded-2xl p-5 hover:border-khazanah-green/40 transition">
                                    <div className="flex items-center gap-2 text-khazanah-green font-bold text-sm sm:text-base mb-2">
                                        <Users size={18} />
                                        <h3>A. Data Identitas & Kontak Pribadi</h3>
                                    </div>
                                    <ul className="text-xs sm:text-sm text-gray-600 space-y-1.5 pl-5 list-disc leading-relaxed">
                                        <li><strong>Nama Lengkap & Nama Panggilan:</strong> Digunakan untuk identitas keanggotaan dan sapaan resmi dalam komunitas.</li>
                                        <li><strong>Nomor WhatsApp:</strong> Media komunikasi utama untuk memasukkan Anda ke dalam grup WhatsApp eksklusif komunitas serta mengirimkan pengingat agenda kajian.</li>
                                        <li><strong>Alamat Email & Akun Instagram:</strong> (Khusus Leader atau korespondensi resmi) untuk komunikasi dokumen, verifikasi profil, dan koordinasi kepanitiaan.</li>
                                        <li><strong>Jenis Kelamin:</strong> Memastikan penempatan grup komunitas terpisah dan menjaga adab syar'i (Sahabat Sholehah khusus muslimah; Sobat Jofisah untuk ikhwan dan akhwat).</li>
                                    </ul>
                                </div>

                                {/* Kategori B */}
                                <div className="border border-gray-200 rounded-2xl p-5 hover:border-khazanah-green/40 transition">
                                    <div className="flex items-center gap-2 text-khazanah-green font-bold text-sm sm:text-base mb-2">
                                        <FileText size={18} />
                                        <h3>B. Data Demografi & Latar Belakang</h3>
                                    </div>
                                    <ul className="text-xs sm:text-sm text-gray-600 space-y-1.5 pl-5 list-disc leading-relaxed">
                                        <li><strong>Generasi / Rentang Usia:</strong> Kategori usia (Pelajar &lt;18, Gen Z 18-27, Milenial 28-43) untuk pemetaan program yang sesuai fase hidup.</li>
                                        <li><strong>Kota / Domisili:</strong> Mengetahui persebaran wilayah anggota guna perencanaan program offline atau meet-up regional di masa depan.</li>
                                        <li><strong>Aktivitas Saat Ini:</strong> Status kegiatan (Pelajar, Mahasiswa, Bekerja, Wirausaha, Ibu Rumah Tangga, Pencari Kerja) untuk menyesuaikan jadwal kajian.</li>
                                        <li><strong>Tempat & Tanggal Lahir (Khusus Leader):</strong> Untuk verifikasi syarat batas minimal usia pendaftar (17 tahun ke atas).</li>
                                    </ul>
                                </div>

                                {/* Kategori C */}
                                <div className="border border-rose-200 bg-rose-50/30 rounded-2xl p-5 hover:border-rose-400 transition">
                                    <div className="flex items-center gap-2 text-rose-700 font-bold text-sm sm:text-base mb-2">
                                        <Heart size={18} />
                                        <h3>C. Data Aspirasi, Harapan & Kegalauan Hidup (Data Sensitif/Rahasia)</h3>
                                    </div>
                                    <p className="text-xs text-rose-800 font-medium mb-3 leading-relaxed">
                                        Bagian ini berisi curahan hati Anda yang kami perlakukan dengan tingkat kerahasiaan paling ketat:
                                    </p>
                                    <ul className="text-xs sm:text-sm text-gray-700 space-y-1.5 pl-5 list-disc leading-relaxed">
                                        <li><strong>Kegalauan / Tantangan Terbesar:</strong> Isu spiritual, quarter-life crisis, jodoh, keluarga, atau mental health yang sedang Anda hadapi.</li>
                                        <li><strong>Harapan & Tujuan Bergabung:</strong> Ekspektasi Anda saat memutuskan menjadi bagian dari Khazanah.</li>
                                        <li><strong>Usulan Topik Kajian & Rekomendasi Narasumber:</strong> Aspirasi tema materi dan ustadz/ustadzah idola Anda.</li>
                                    </ul>
                                </div>

                                {/* Kategori D */}
                                <div className="border border-gray-200 rounded-2xl p-5 hover:border-khazanah-green/40 transition">
                                    <div className="flex items-center gap-2 text-khazanah-green font-bold text-sm sm:text-base mb-2">
                                        <CheckCircle2 size={18} />
                                        <h3>D. Berkas & Bukti Pendukung</h3>
                                    </div>
                                    <ul className="text-xs sm:text-sm text-gray-600 space-y-1.5 pl-5 list-disc leading-relaxed">
                                        <li><strong>Bukti Tangkapan Layar Follow Media Sosial:</strong> Bagi pendaftar member gratis, sebagai syarat komitmen mendukung syiar Khazanah.</li>
                                        <li><strong>Bukti Transfer Infaq & Nominal:</strong> Bagi member yang berinfaq sukarela, digunakan semata-mata untuk verifikasi pencatatan bendahara.</li>
                                        <li><strong>Curriculum Vitae (PDF - Khusus Leader):</strong> Riwayat pendidikan, pengalaman organisasi, dan portofolio untuk seleksi kompetensi pengurus.</li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* SECTION 3: TUJUAN PENGGUNAAN DATA */}
                        <section id="purpose" className="bg-white rounded-3xl p-8 border border-gray-200/80 shadow-xs space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-xl bg-khazanah-light text-khazanah-green flex items-center justify-center font-bold text-sm">
                                    3
                                </span>
                                <h2 className="text-xl sm:text-2xl font-black font-outfit text-gray-900">
                                    Tujuan Penggunaan Data Pribadi
                                </h2>
                            </div>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-normal">
                                Seluruh informasi yang Anda sampaikan kepada Khazanah hanya dipergunakan untuk maksud dan tujuan mulia berikut:
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                                <div className="bg-[#FAF9F5] p-4 rounded-2xl border border-gray-200">
                                    <div className="font-bold text-xs sm:text-sm text-khazanah-dark mb-1 flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-khazanah-gold" />
                                        1. Administrasi Keanggotaan
                                    </div>
                                    <p className="text-xs text-gray-600 leading-relaxed">
                                        Memverifikasi keabsahan data nomor WhatsApp serta mengelompokkan ke grup diskusi WhatsApp yang kondusif.
                                    </p>
                                </div>
                                <div className="bg-[#FAF9F5] p-4 rounded-2xl border border-gray-200">
                                    <div className="font-bold text-xs sm:text-sm text-khazanah-dark mb-1 flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-khazanah-gold" />
                                        2. Perumusan Kurikulum Kajian
                                    </div>
                                    <p className="text-xs text-gray-600 leading-relaxed">
                                        Menyesuaikan topik webinar, sharing session, dan buku panduan dakwah berdasarkan keresahan riil anggota terbanyak.
                                    </p>
                                </div>
                                <div className="bg-[#FAF9F5] p-4 rounded-2xl border border-gray-200">
                                    <div className="font-bold text-xs sm:text-sm text-khazanah-dark mb-1 flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-khazanah-gold" />
                                        3. Penyampaian Info & Syiar
                                    </div>
                                    <p className="text-xs text-gray-600 leading-relaxed">
                                        Mengirimkan broadcast pengingat sholat, kutipan hadits, artikel harian, dan notifikasi jadwal event komunitas.
                                    </p>
                                </div>
                                <div className="bg-[#FAF9F5] p-4 rounded-2xl border border-gray-200">
                                    <div className="font-bold text-xs sm:text-sm text-khazanah-dark mb-1 flex items-center gap-1.5">
                                        <span className="w-2 h-2 rounded-full bg-khazanah-gold" />
                                        4. Seleksi Khazanah Leader
                                    </div>
                                    <p className="text-xs text-gray-600 leading-relaxed">
                                        Menilai kecocokan minat, motivasi, dan keahlian kandidat pelamar kepengurusan komunitas.
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* SECTION 4: KERAHASIAAN & RUANG CURHAT */}
                        <section id="confidentiality" className="bg-white rounded-3xl p-8 border border-gray-200/80 shadow-xs space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-xl bg-pink-50 text-roseSholehah flex items-center justify-center font-bold text-sm">
                                    4
                                </span>
                                <h2 className="text-xl sm:text-2xl font-black font-outfit text-gray-900">
                                    Kerahasiaan Curahan Hati & Ruang Curhat
                                </h2>
                            </div>
                            <div className="bg-gradient-to-r from-pink-50 via-white to-pink-50/30 p-5 rounded-2xl border border-pink-200 text-gray-800 space-y-2">
                                <div className="flex items-center gap-2 text-rose-700 font-bold text-sm">
                                    <Eye size={18} />
                                    <span>Prinsip Perlindungan Rahasia Ukhuwah</span>
                                </div>
                                <p className="text-xs sm:text-sm text-gray-700 leading-relaxed font-normal">
                                    Kami memahami bahwa menceritakan keresahan hidup (seputar kegalauan spiritual, pergaulan, pernikahan, atau keluarga) membutuhkan keberanian dan rasa percaya yang tinggi.
                                </p>
                            </div>
                            <ul className="text-xs sm:text-sm text-gray-600 space-y-2.5 pl-5 list-disc leading-relaxed">
                                <li><strong>Tidak Dipublikasikan:</strong> Isian keresahan/concerns pada formulir pendaftaran bersifat 100% internal. Kami TIDAK PERNAH membagikan nama Anda bersama masalah yang Anda tuliskan kepada publik atau anggota lain.</li>
                                <li><strong>Analisis Anonim & Agregat:</strong> Kegalauan Anda hanya dirangkum dalam bentuk statistik umum tanpa nama (misalnya: <em>"40% member membutuhkan materi kesiapan pra-nikah"</em>) untuk keperluan penentuan tema materi.</li>
                                <li><strong>Ruang Curhat Publik:</strong> Bagi cerita yang dikirimkan melalui fitur Ruang Curhat di website kami, publikasi hanya dilakukan jika pengirim memilih status publik, dan identitas asli (nama/kontak) akan disamarkan sebagai anonim atau inisial sesuai pilihan Anda.</li>
                            </ul>
                        </section>

                        {/* SECTION 5: PEMBAGIAN DATA & PIHAK KETIGA */}
                        <section id="sharing" className="bg-white rounded-3xl p-8 border border-gray-200/80 shadow-xs space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-xl bg-khazanah-light text-khazanah-green flex items-center justify-center font-bold text-sm">
                                    5
                                </span>
                                <h2 className="text-xl sm:text-2xl font-black font-outfit text-gray-900">
                                    Kebijakan Pembagian Data & Pihak Ketiga
                                </h2>
                            </div>
                            <div className="bg-emerald-50/80 border border-emerald-200 rounded-2xl p-4.5 flex items-start gap-3">
                                <ShieldCheck className="text-khazanah-green shrink-0 mt-0.5" size={20} />
                                <div>
                                    <h3 className="font-bold text-sm text-khazanah-dark mb-1">
                                        Zero-Commercialization Guarantee
                                    </h3>
                                    <p className="text-xs text-gray-700 leading-relaxed">
                                        Khazanah <strong>TIDAK PERNAH MENJUAL, MENYEWAKAN, MENUKAR, ATAU MEMPERDAGANGKAN</strong> data pribadi anggota kepada perusahaan iklan, agen telemarketing, pihak pinjol, atau pihak komersial mana pun.
                                    </p>
                                </div>
                            </div>
                            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                                Data Anda hanya dapat diakses oleh pihak berikut dalam batas wewenang yang ketat:
                            </p>
                            <ul className="text-xs sm:text-sm text-gray-600 space-y-2 pl-5 list-disc leading-relaxed">
                                <li><strong>Tim Pengurus Resmi Khazanah:</strong> Admin verifikasi pendaftaran dan koordinator grup yang telah menandatangani pakta integritas kerahasiaan.</li>
                                <li><strong>Penyedia Infrastruktur Teknologi Terpercaya:</strong> Layanan database server dan hosting yang menerapkan protokol keamanan enkripsi standar industri.</li>
                                <li><strong>Kewajiban Hukum:</strong> Kami hanya akan membuka informasi jika diwajibkan oleh peraturan perundang-undangan Republik Indonesia yang sah atau perintah pengadilan resmi.</li>
                            </ul>
                        </section>

                        {/* SECTION 6: HAK-HAK ANDA SEBAGAI MEMBER */}
                        <section id="rights" className="bg-white rounded-3xl p-8 border border-gray-200/80 shadow-xs space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-xl bg-khazanah-light text-khazanah-green flex items-center justify-center font-bold text-sm">
                                    6
                                </span>
                                <h2 className="text-xl sm:text-2xl font-black font-outfit text-gray-900">
                                    Hak-Hak Anda atas Data Pribadi
                                </h2>
                            </div>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-normal">
                                Sesuai dengan Undang-Undang Pelindungan Data Pribadi (UU PDP), Anda sebagai pemilik data memiliki hak penuh berikut:
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 pt-1">
                                <div className="p-4 rounded-2xl border border-gray-200 bg-white shadow-2xs">
                                    <h3 className="font-bold text-xs sm:text-sm text-gray-900 mb-1 flex items-center gap-1.5">
                                        <CheckCircle2 size={15} className="text-khazanah-green" /> Hak Mengakses Data
                                    </h3>
                                    <p className="text-xs text-gray-600 leading-relaxed">
                                        Anda berhak menanyakan dan mengonfirmasi data pribadi apa saja yang tercatat dalam sistem kami.
                                    </p>
                                </div>
                                <div className="p-4 rounded-2xl border border-gray-200 bg-white shadow-2xs">
                                    <h3 className="font-bold text-xs sm:text-sm text-gray-900 mb-1 flex items-center gap-1.5">
                                        <CheckCircle2 size={15} className="text-khazanah-green" /> Hak Memperbarui Data
                                    </h3>
                                    <p className="text-xs text-gray-600 leading-relaxed">
                                        Jika Anda berganti nomor WhatsApp, domisili, atau nama panggilan, Anda berhak meminta pembaruan data kapan saja.
                                    </p>
                                </div>
                                <div className="p-4 rounded-2xl border border-gray-200 bg-white shadow-2xs">
                                    <h3 className="font-bold text-xs sm:text-sm text-gray-900 mb-1 flex items-center gap-1.5">
                                        <CheckCircle2 size={15} className="text-khazanah-green" /> Hak Penghapusan (Right to be Forgotten)
                                    </h3>
                                    <p className="text-xs text-gray-600 leading-relaxed">
                                        Anda berhak meminta agar data pendaftaran Anda dihapus secara permanen dari basis data kami jika Anda memutuskan tidak lagi menjadi anggota.
                                    </p>
                                </div>
                                <div className="p-4 rounded-2xl border border-gray-200 bg-white shadow-2xs">
                                    <h3 className="font-bold text-xs sm:text-sm text-gray-900 mb-1 flex items-center gap-1.5">
                                        <CheckCircle2 size={15} className="text-khazanah-green" /> Hak Berhenti Berlangganan
                                    </h3>
                                    <p className="text-xs text-gray-600 leading-relaxed">
                                        Anda bebas keluar dari grup WhatsApp komunitas atau meminta agar nomor Anda tidak menerima pesan siaran/broadcast Khazanah.
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* SECTION 7: PENYIMPANAN & KEAMANAN */}
                        <section id="retention" className="bg-white rounded-3xl p-8 border border-gray-200/80 shadow-xs space-y-4">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-xl bg-khazanah-light text-khazanah-green flex items-center justify-center font-bold text-sm">
                                    7
                                </span>
                                <h2 className="text-xl sm:text-2xl font-black font-outfit text-gray-900">
                                    Penyimpanan & Keamanan Data
                                </h2>
                            </div>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-normal">
                                Kami menerapkan langkah-langkah perlindungan teknis dan organisasional untuk menjaga keamanan data Anda, antara lain:
                            </p>
                            <ul className="text-xs sm:text-sm text-gray-600 space-y-2 pl-5 list-disc leading-relaxed">
                                <li><strong>Enkripsi Komunikasi:</strong> Seluruh pertukaran data antara browser Anda dan situs Khazanah diamankan dengan sertifikat SSL/HTTPS (TLS).</li>
                                <li><strong>Akses Terproteksi Sandi & JWT:</strong> Dashboard manajemen data anggota Khazanah dilindungi dengan sistem autentikasi bertingkat (Protected Route & Hash Password).</li>
                                <li><strong>Retensi Data Wajar:</strong> Data pendaftaran disimpan selama Anda menjadi anggota aktif atau sampai Anda mengajukan permohonan penghapusan akun. Berkas bukti transfer dan bukti follow dapat dihapus secara berkala dari penyimpanan server untuk efisiensi dan keamanan.</li>
                            </ul>
                        </section>

                        {/* SECTION 8: KONTAK & PENGADUAN */}
                        <section id="contact" className="bg-white rounded-3xl p-8 border border-gray-200/80 shadow-xs space-y-6">
                            <div className="flex items-center gap-3">
                                <span className="w-8 h-8 rounded-xl bg-khazanah-light text-khazanah-green flex items-center justify-center font-bold text-sm">
                                    8
                                </span>
                                <h2 className="text-xl sm:text-2xl font-black font-outfit text-gray-900">
                                    Kontak Layanan & Pengaduan Privasi
                                </h2>
                            </div>
                            <p className="text-sm sm:text-base text-gray-600 leading-relaxed font-normal">
                                Jika Anda memiliki pertanyaan mengenai Kebijakan Privasi ini, ingin memperbarui data, atau ingin mengajukan permohonan penghapusan data, silakan hubungi saluran resmi kami:
                            </p>

                            <div className="bg-[#FAF9F5] rounded-2xl p-6 border border-gray-200 space-y-4">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-200">
                                    <div>
                                        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">WhatsApp Admin Resmi</p>
                                        <p className="text-sm sm:text-base font-bold text-gray-900 mt-0.5">+62 *** *** ***</p>
                                    </div>
                                    <a
                                        href="https://wa.me/6281234567890?text=Halo%20Admin%20Khazanah,%20saya%20ingin%20mengajukan%20pertanyaan/permintaan%20terkait%20Kebijakan%20Privasi%20data%20saya"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center justify-center gap-2 bg-emerald text-white px-4 py-2.5 rounded-xl font-bold text-xs hover:bg-emerald-dark transition shadow-xs"
                                    >
                                        <MessageCircle size={16} /> Hubungi Admin Privasi
                                    </a>
                                </div>

                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div>
                                        <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Email Perlindungan Data</p>
                                        <p className="text-sm sm:text-base font-bold text-gray-900 mt-0.5">**@*****.***</p>
                                    </div>
                                    <a
                                        href="mailto:halo@khazanahalwahda.com?subject=Permohonan%20Data%20Privasi%20Khazanah"
                                        className="inline-flex items-center justify-center gap-2 bg-white text-gray-800 border border-gray-300 px-4 py-2.5 rounded-xl font-bold text-xs hover:bg-gray-50 transition shadow-2xs"
                                    >
                                        <Mail size={16} /> Kirim Email Resmi
                                    </a>
                                </div>
                            </div>

                            {/* PERSETUJUAN PENUTUP */}
                            <div className="p-5 rounded-2xl bg-khazanah-light border border-khazanah-green/20 flex items-start gap-3.5">
                                <HeartHandshake size={22} className="text-khazanah-green shrink-0 mt-0.5" />
                                <p className="text-xs sm:text-sm text-khazanah-dark leading-relaxed font-medium">
                                    Dengan terus menggunakan platform dan berpartisipasi dalam komunitas kami, Anda menegaskan persetujuan Anda terhadap Kebijakan Privasi ini. Semoga Allah memberkahi setiap langkah kebaikan dan persaudaraan kita di jalan dakwah.
                                </p>
                            </div>

                            {/* Tombol Kembali / Daftar */}
                            <div className="pt-4 flex flex-wrap gap-3">
                                <Link
                                    to="/join"
                                    className="px-6 py-3 rounded-xl bg-khazanah-green text-white font-bold text-xs sm:text-sm hover:bg-khazanah-dark transition shadow-md shadow-khazanah-green/20"
                                >
                                    Kembali ke Pilihan Komunitas
                                </Link>
                                <Link
                                    to="/"
                                    className="px-6 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold text-xs sm:text-sm hover:bg-gray-200 transition"
                                >
                                    Halaman Utama
                                </Link>
                            </div>
                        </section>

                    </div>
                </div>
            </AnimatedSection>
        </div>
    );
};

export default PrivacyPolicy;
